import { Graphics } from 'pixi.js';
import type { Vec2 } from './Vec2';
import { vec2, sub, dot, normalize, length } from './Vec2';
import type { CollisionResult, Terrain } from './Physics';
import { GAME_WIDTH, GAME_HEIGHT } from '../constants';

export interface Segment {
  a: Vec2;
  b: Vec2;
}

export interface TerrainConfig {
  polygons: Vec2[][];
  fillColor: number;
  verticalWrap?: boolean;
}

export class TerrainData implements Terrain {
  readonly polygons: Vec2[][];
  readonly fillColor: number;
  readonly verticalWrap: boolean;

  constructor(config: TerrainConfig) {
    this.polygons = config.polygons;
    this.fillColor = config.fillColor;
    this.verticalWrap = config.verticalWrap ?? false;
  }

  /** Test if a point is inside a polygon using ray casting */
  pointInPolygon(point: Vec2, polygon: Vec2[]): boolean {
    let inside = false;
    const n = polygon.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const pi = polygon[i]!;
      const pj = polygon[j]!;
      if (
        (pi.y > point.y) !== (pj.y > point.y) &&
        point.x < ((pj.x - pi.x) * (point.y - pi.y)) / (pj.y - pi.y) + pi.x
      ) {
        inside = !inside;
      }
    }
    return inside;
  }

  /** Get the outward normal for an edge A→B (CW winding, y-down) */
  edgeOutwardNormal(a: Vec2, b: Vec2): Vec2 {
    return normalize(vec2(b.y - a.y, -(b.x - a.x)));
  }

  /** Find the closest point on segment AB to point P */
  private closestPointOnSegment(p: Vec2, a: Vec2, b: Vec2): Vec2 {
    const ab = sub(b, a);
    const ap = sub(p, a);
    const abLenSq = dot(ab, ab);
    if (abLenSq === 0) return a;
    const t = Math.max(0, Math.min(1, dot(ap, ab) / abLenSq));
    return vec2(a.x + ab.x * t, a.y + ab.y * t);
  }

  /** Collide a circle (ball) against all polygons */
  collide(center: Vec2, radius: number): CollisionResult | null {
    let deepest: CollisionResult | null = null;

    // Check at the ball's position and also shifted for horizontal wrapping
    const xOffsets = [0];
    if (center.x < radius) xOffsets.push(GAME_WIDTH);
    if (center.x > GAME_WIDTH - radius) xOffsets.push(-GAME_WIDTH);

    // Check Y offsets only when vertical wrapping is enabled
    const yOffsets = [0];
    if (this.verticalWrap) {
      if (center.y < radius) yOffsets.push(GAME_HEIGHT);
      if (center.y > GAME_HEIGHT - radius) yOffsets.push(-GAME_HEIGHT);
    }

    for (const offsetX of xOffsets) {
      for (const offsetY of yOffsets) {
        const shifted = vec2(center.x + offsetX, center.y + offsetY);
        const result = this.collideAt(shifted, radius);
        if (result && (deepest === null || result.depth > deepest.depth)) {
          deepest = result;
        }
      }
    }

    return deepest;
  }

  /** Collide at a specific position (no wrapping logic) */
  private collideAt(center: Vec2, radius: number): CollisionResult | null {
    let deepest: CollisionResult | null = null;

    for (const polygon of this.polygons) {
      const result = this.collidePolygon(center, radius, polygon);
      if (result && (deepest === null || result.depth > deepest.depth)) {
        deepest = result;
      }
    }

    return deepest;
  }

  /** Collide a circle against a single polygon */
  private collidePolygon(center: Vec2, radius: number, polygon: Vec2[]): CollisionResult | null {
    const n = polygon.length;
    if (n < 3) return null;

    // Check if center is inside the polygon
    if (this.pointInPolygon(center, polygon)) {
      // Find nearest edge and push out along its outward normal
      let minDist = Infinity;
      let bestNormal: Vec2 = vec2(0, -1);

      for (let i = 0; i < n; i++) {
        const a = polygon[i]!;
        const b = polygon[(i + 1) % n]!;
        const closest = this.closestPointOnSegment(center, a, b);
        const diff = sub(center, closest);
        const dist = length(diff);

        if (dist < minDist) {
          minDist = dist;
          bestNormal = this.edgeOutwardNormal(a, b);
        }
      }

      return { normal: bestNormal, depth: minDist + radius };
    }

    // Center is outside — check distance to each edge
    let minDist = Infinity;
    let bestNormal: Vec2 | null = null;

    for (let i = 0; i < n; i++) {
      const a = polygon[i]!;
      const b = polygon[(i + 1) % n]!;
      const closest = this.closestPointOnSegment(center, a, b);
      const diff = sub(center, closest);
      const dist = length(diff);

      if (dist < radius && dist < minDist) {
        minDist = dist;
        // Normal points from closest point toward center
        if (dist > 0) {
          bestNormal = normalize(diff);
        } else {
          bestNormal = this.edgeOutwardNormal(a, b);
        }
      }
    }

    if (bestNormal) {
      return { normal: bestNormal, depth: radius - minDist };
    }

    return null;
  }

  /** Get the surface Y coordinate at a given X position.
   *  Uses the first polygon's surface points (all except the last 2 bottom-edge closing corners).
   *  Returns null if x is outside the surface range or polygon has < 3 points. */
  getSurfaceY(x: number): number | null {
    const polygon = this.polygons[0];
    if (!polygon || polygon.length < 3) return null;

    // Surface points are all except the last 2 (bottom-right, bottom-left closing corners)
    const surfaceCount = polygon.length - 2;
    if (surfaceCount < 2) return null;

    // Find the segment spanning x
    for (let i = 0; i < surfaceCount - 1; i++) {
      const a = polygon[i]!;
      const b = polygon[i + 1]!;
      const minX = Math.min(a.x, b.x);
      const maxX = Math.max(a.x, b.x);

      if (x >= minX && x <= maxX) {
        // Linear interpolation
        if (a.x === b.x) return a.y;
        const t = (x - a.x) / (b.x - a.x);
        return a.y + t * (b.y - a.y);
      }
    }

    return null;
  }

  /** Create a PixiJS Graphics object for rendering */
  render(): Graphics {
    const gfx = new Graphics();

    for (const polygon of this.polygons) {
      if (polygon.length < 3) continue;
      gfx.moveTo(polygon[0]!.x, polygon[0]!.y);
      for (let i = 1; i < polygon.length; i++) {
        gfx.lineTo(polygon[i]!.x, polygon[i]!.y);
      }
      gfx.closePath();
      gfx.fill(this.fillColor);
    }

    return gfx;
  }
}

/** Cut a rectangular notch into the first terrain polygon at the given position.
 *  Modifies the polygon in-place. The notch creates walls and a floor that the
 *  ball can collide with naturally via the existing collision system. */
export function cutHole(
  polygons: Vec2[][],
  holeX: number,
  holeSurfaceY: number,
  holeWidth: number,
  holeDepth: number
): void {
  const polygon = polygons[0];
  if (!polygon || polygon.length < 4) return;

  const halfW = holeWidth / 2;
  const leftX = holeX - halfW;
  const rightX = holeX + halfW;
  const bottomY = holeSurfaceY + holeDepth;

  // Surface points are all except the last 2 (bottom-right, bottom-left closing corners)
  const surfaceCount = polygon.length - 2;
  const bottomCorners = polygon.slice(surfaceCount);

  // Interpolate surface Y at left and right edges of hole
  function getSurfaceYAt(x: number): number {
    for (let i = 0; i < surfaceCount - 1; i++) {
      const a = polygon![i]!;
      const b = polygon![i + 1]!;
      if ((a.x <= x && x <= b.x) || (b.x <= x && x <= a.x)) {
        if (a.x === b.x) return a.y;
        const t = (x - a.x) / (b.x - a.x);
        return a.y + t * (b.y - a.y);
      }
    }
    return holeSurfaceY;
  }

  const leftSurfaceY = getSurfaceYAt(leftX);
  const rightSurfaceY = getSurfaceYAt(rightX);

  // Split surface points into left part (x <= leftX) and right part (x >= rightX)
  const leftPart: Vec2[] = [];
  const rightPart: Vec2[] = [];

  for (let i = 0; i < surfaceCount; i++) {
    const p = polygon[i]!;
    if (p.x <= leftX) {
      leftPart.push(p);
    }
    if (p.x >= rightX) {
      rightPart.push(p);
    }
  }

  // Add interpolated edge point if the last left point isn't exactly at leftX
  if (leftPart.length === 0 || leftPart[leftPart.length - 1]!.x < leftX) {
    leftPart.push(vec2(leftX, leftSurfaceY));
  }

  // Add interpolated edge point if the first right point isn't exactly at rightX
  if (rightPart.length === 0 || rightPart[0]!.x > rightX) {
    rightPart.unshift(vec2(rightX, rightSurfaceY));
  }

  // Build new polygon with notch: left surface → left wall down → hole floor → right wall up → right surface → bottom corners
  const newPolygon: Vec2[] = [
    ...leftPart,
    vec2(leftX, bottomY),   // left wall bottom
    vec2(rightX, bottomY),  // right wall bottom
    ...rightPart,
    ...bottomCorners,
  ];

  polygons[0] = newPolygon;
}

/** Create demo terrain with layered sine-wave hills */
export function createDemoTerrain(): TerrainData {
  const points: Vec2[] = [];

  // Surface line from left to right
  for (let x = 0; x < GAME_WIDTH; x++) {
    const y =
      GAME_HEIGHT * 0.65 +
      Math.sin((x / GAME_WIDTH) * Math.PI * 2) * 80 +
      Math.sin((x / GAME_WIDTH) * Math.PI * 4) * 40 +
      Math.sin((x / GAME_WIDTH) * Math.PI * 6) * 20;
    points.push(vec2(x, y));
  }

  // Close along bottom of screen (CW winding: surface left→right, then bottom-right→bottom-left)
  points.push(vec2(GAME_WIDTH, GAME_HEIGHT));
  points.push(vec2(0, GAME_HEIGHT));

  return new TerrainData({
    polygons: [points],
    fillColor: 0x4a8c3f,
  });
}

/** Create demo cave terrain with floor and ceiling polygons */
export function createDemoCaveTerrain(): TerrainData {
  const floorPoints: Vec2[] = [];
  const ceilingPoints: Vec2[] = [];

  // Floor surface
  for (let x = 0; x < GAME_WIDTH; x++) {
    const y =
      GAME_HEIGHT * 0.75 +
      Math.sin((x / GAME_WIDTH) * Math.PI * 2) * 60 +
      Math.sin((x / GAME_WIDTH) * Math.PI * 6) * 25;
    floorPoints.push(vec2(x, y));
  }
  // Close along bottom (CW)
  floorPoints.push(vec2(GAME_WIDTH, GAME_HEIGHT));
  floorPoints.push(vec2(0, GAME_HEIGHT));

  // Ceiling surface (CW winding: top-left→top-right, then surface right→left)
  ceilingPoints.push(vec2(0, 0));
  ceilingPoints.push(vec2(GAME_WIDTH, 0));
  for (let x = GAME_WIDTH - 1; x >= 0; x--) {
    const y =
      GAME_HEIGHT * 0.25 -
      Math.sin((x / GAME_WIDTH) * Math.PI * 2) * 60 -
      Math.sin((x / GAME_WIDTH) * Math.PI * 6) * 25;
    ceilingPoints.push(vec2(x, y));
  }

  return new TerrainData({
    polygons: [floorPoints, ceilingPoints],
    fillColor: 0x8b6914,
    verticalWrap: true,
  });
}

export class SegmentTerrain implements Terrain {
  readonly segments: Segment[];
  readonly verticalWrap: boolean;
  /** Index of the segment that had the deepest collision in the last collide() call, or -1 if none. */
  lastCollidedSegmentIndex: number = -1;

  constructor(segments: Segment[], verticalWrap?: boolean) {
    this.segments = segments;
    this.verticalWrap = verticalWrap ?? false;
  }

  /** Find the closest point on segment AB to point P */
  private closestPointOnSegment(p: Vec2, a: Vec2, b: Vec2): Vec2 {
    const ab = sub(b, a);
    const ap = sub(p, a);
    const abLenSq = dot(ab, ab);
    if (abLenSq === 0) return a;
    const t = Math.max(0, Math.min(1, dot(ap, ab) / abLenSq));
    return vec2(a.x + ab.x * t, a.y + ab.y * t);
  }

  /** Get the outward normal for a segment (perpendicular, pointing "left" of A→B in screen coords) */
  private segmentNormal(a: Vec2, b: Vec2): Vec2 {
    return normalize(vec2(b.y - a.y, -(b.x - a.x)));
  }

  collide(center: Vec2, radius: number): CollisionResult | null {
    let deepest: CollisionResult | null = null;
    this.lastCollidedSegmentIndex = -1;

    const xOffsets = [0];
    if (center.x < radius) xOffsets.push(GAME_WIDTH);
    if (center.x > GAME_WIDTH - radius) xOffsets.push(-GAME_WIDTH);

    const yOffsets = [0];
    if (this.verticalWrap) {
      if (center.y < radius) yOffsets.push(GAME_HEIGHT);
      if (center.y > GAME_HEIGHT - radius) yOffsets.push(-GAME_HEIGHT);
    }

    for (const offsetX of xOffsets) {
      for (const offsetY of yOffsets) {
        const shifted = vec2(center.x + offsetX, center.y + offsetY);
        const hit = this.collideAt(shifted, radius);
        if (hit && (deepest === null || hit.result.depth > deepest.depth)) {
          deepest = hit.result;
          this.lastCollidedSegmentIndex = hit.segmentIndex;
        }
      }
    }

    return deepest;
  }

  private collideAt(center: Vec2, radius: number): { result: CollisionResult; segmentIndex: number } | null {
    let deepest: CollisionResult | null = null;
    let deepestIndex = -1;

    for (let si = 0; si < this.segments.length; si++) {
      const seg = this.segments[si]!;
      const closest = this.closestPointOnSegment(center, seg.a, seg.b);
      const diff = sub(center, closest);
      const dist = length(diff);

      if (dist < radius) {
        let normal: Vec2;
        if (dist > 1e-6) {
          normal = normalize(diff);
        } else {
          // Ball center is exactly on the segment — use perpendicular normal
          normal = this.segmentNormal(seg.a, seg.b);
        }
        const depth = radius - dist;
        if (deepest === null || depth > deepest.depth) {
          deepest = { normal, depth };
          deepestIndex = si;
        }
      }
    }

    if (deepest) {
      return { result: deepest, segmentIndex: deepestIndex };
    }
    return null;
  }

  /** Render segments as white lines */
  render(): Graphics {
    const gfx = new Graphics();

    for (const seg of this.segments) {
      gfx.moveTo(seg.a.x, seg.a.y);
      gfx.lineTo(seg.b.x, seg.b.y);
      gfx.stroke({ width: 2, color: 0xffffff });
    }

    return gfx;
  }
}
